import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { Box, Stack, Theme, Typography, useTheme } from "@mui/material";
import React from "react";
import { MdSearch } from "react-icons/md";
import TuneIcon from "@mui/icons-material/Tune";
import { ClearFilterButton } from "./ClearFilterButton";

interface Props {
  isBottomPanelOpen?: boolean;
  isHidden?: boolean;
  isClearFiltersButtonVisible?: boolean;
  renderButtons: () => React.ReactElement;
  onMouseEnter: (a: any) => void;
  onMouseLeave: (a: any) => void;
  onClearFilters?: () => void;
}

export const SearchBar = React.forwardRef(
  (
    {
      isBottomPanelOpen,
      isHidden,
      isClearFiltersButtonVisible,
      renderButtons,
      onMouseEnter,
      onMouseLeave,
      onClearFilters,
    }: Props,
    ref: any
  ) => {
    const theme = useTheme();
    const styles = useStyles(theme, { isBottomPanelOpen, isHidden });
    const { md: mdDown } = useCssMediaQuery("down");

    return (
      <Box
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={ref}
        sx={styles.searchBox}
      >
        <Stack
          direction={"row"}
          spacing={1}
          width={isClearFiltersButtonVisible ? "100%" : "auto"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={!mdDown ? 1 : 0}
            p={2}
            pr={0}
          >
            <Box
              fontSize={"24px"}
              width={"24px"}
              height={"24px"}
              sx={{ transform: "translateY(-2px)" }}
            >
              {/* <MdSearch /> */}
              <TuneIcon />
            </Box>
            {!mdDown ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  Filters:
                </Typography>
              </Box>
            ) : null}
          </Stack>

          <Box
            width={isClearFiltersButtonVisible ? "100%" : "auto"}
            sx={styles.scrollContainer}
          >
            <Box sx={styles.scrollContent}>{renderButtons()}</Box>
          </Box>

          {isClearFiltersButtonVisible ? (
            <>
              <Box flex={1} />
              <Stack p={2} pl={0}>
                <ClearFilterButton onClick={() => onClearFilters?.()} />
              </Stack>
            </>
          ) : null}
        </Stack>
      </Box>
    );
  }
);

SearchBar.displayName = "SearchBar";

function useStyles(theme: Theme, states: Partial<Props>) {
  return {
    searchBox: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "8px",
      // padding: "16px",
      width: "100%",
      transition: "all 0.3s ease",
      position: "relative",
      borderBottomRightRadius: !!states?.isBottomPanelOpen ? 0 : "8px",
      borderBottomLeftRadius: !!states?.isBottomPanelOpen ? 0 : "8px",

      opacity: states?.isHidden ? 0 : 1,
      pointerEvents: states?.isHidden ? "none" : "all",

      "&:hover": {
        boxShadow: "0px 16px 32px 0px rgba(0,0,0,0.1)",
      },

      [theme.breakpoints.down("md")]: {
        display: states?.isHidden ? "none" : "block",
      },
    },

    scrollContainer: {
      paddingLeft: 0,
      // outline: "1px solid green",
      position: "relative",
      overflowX: "auto",
      overflowY: "visible !important",
      "&::-webkit-scrollbar": {
        display: "none",
      },

      [theme.breakpoints.down("md")]: {
        flexGrow: 1,
      },
    },
    scrollContent: {
      position: "relative",
      top: "50%",
      left: "0",
      transform: "translateY(-50%)",
      transition: "width 0.4s ease",

      [theme.breakpoints.down("md")]: {
        position: "absolute",
      },
    },
  };
}
